import clsx from 'clsx'
import { html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { FeedbackType } from '@/components/feedback/feedback'
import { CommonSize, Size } from '@/dap-design-system'

type Constructor<T = Record<string, unknown>> = {
  new (...args: any[]): T
  prototype: T
}

export interface FeedbackElementInterface {
  feedbackSize?: CommonSize
  feedback: string
  feedbackType?: FeedbackType
  feedbackSubtle?: boolean
  feedbackNoMargin?: boolean
  feedbackId?: string
  renderFeedback(content?: unknown): unknown
}

export function FeedbackMixin<T extends Constructor<LitElement>>(
  constructor: T,
): T & Constructor<FeedbackElementInterface> {
  class FeedbackElement extends constructor {
    @property() feedbackSize?: Size = 'sm'
    @property() feedback: string
    @property() feedbackType: FeedbackType = 'positive'
    @property({ type: Boolean, reflect: true }) feedbackSubtle = false
    @property({ type: Boolean }) feedbackNoMargin = false
    @property() feedbackId?: string

    renderFeedback() {
      return html`<div
        part="base"
        class=${clsx(
          'feedback',
          `feedback--${this.feedbackType}`,
          `feedback--${this.feedbackSize}`,
          {
            'feedback--subtle': this.feedbackSubtle,
            'feedback--no-margin': this.feedbackNoMargin,
          },
        )}>
        <slot name="icon" class="feedback__icon">
          <dap-ds-icon
            part="icon"
            aria-hidden="true"
            size="${ifDefined(this.feedbackSize) as CommonSize}"
            name="${this._iconType(this.feedbackType)}">
          </dap-ds-icon>
        </slot>
        <div part="text" class="feedback__text" role="alert" aria-live="polite">
          <slot></slot>
        </div>
      </div> `
    }

    private _iconType(type: FeedbackType): string {
      switch (type) {
        case 'positive':
          return `checkbox-circle-${this.feedbackSubtle ? 'line' : 'fill'}`
        case 'negative':
          return `error-warning-${this.feedbackSubtle ? 'line' : 'fill'}`
        case 'warning':
          return `alert-${this.feedbackSubtle ? 'line' : 'fill'}`
        default:
          return `information-${this.feedbackSubtle ? 'line' : 'fill'}`
      }
    }
  }
  return FeedbackElement as Constructor<FeedbackElementInterface> & T
}
